import {useEffect, useMemo, useState} from "react";
import {Box, Stack, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {appStyles} from "../../../../AppStyles.ts";
import {StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../../AppStyledComponents.tsx";
import type {ResourceReference} from "../../../../sdo/ResourceReference.ts";
import ResourceReferenceCard from "./resources/ResourceReferenceCard.tsx";
import ResourceReferenceDetail from "./resources/ResourceReferenceDetail.tsx";

// TODO: Replace with API call to IntelProfileManager.getResourceReferences(profileType, typeKey, defKey, matchHash)
function buildTestData(): ResourceReference[] {
    return [
        {
            uid: "1",
            display: "Quarterly Threat Brief — Q1 2026",
            typeKey: "pages",
            definitionKey: "threat_brief",
            matchHash: "a1b2c3d4",
            frames: [
                {
                    uid: "f1",
                    frameNumber: 1,
                    page: 4, section: 2, paragraph: 1, sentence: 3,
                    originalText: "Subject was identified during the partner intake process and flagged for analyst follow-up.",
                    pointers: [
                        {uid: "p1", typeKey: "user_profile", display: "Subject A"},
                    ],
                },
                {
                    uid: "f2",
                    frameNumber: 2,
                    page: 7, section: 1, paragraph: 4, sentence: 1,
                    originalText: "Cross-reference to network segment 192.168.4.0/24 confirmed via DNS log correlation.",
                    pointers: [],
                },
            ],
            metadata: {author: "j.doe", classification: "internal"},
        },
        {
            uid: "2",
            display: "Open-Source News Article — Reuters",
            typeKey: "article",
            definitionKey: "news_article",
            matchHash: "e5f6a7b8",
            frames: [
                {
                    uid: "f3",
                    frameNumber: 1,
                    page: 1, section: 1, paragraph: 2, sentence: 2,
                    originalText: "The named individual was reported as a key witness in the ongoing investigation.",
                    pointers: [
                        {uid: "p2", typeKey: "user_profile", display: "Subject A"},
                    ],
                },
            ],
            metadata: {source: "reuters.com"},
        },
        {
            uid: "3",
            display: "Internal Memo — 2026-03 Compliance Review",
            typeKey: "pages",
            definitionKey: "memo",
            matchHash: "9c0d1e2f",
            frames: [],
            metadata: {department: "compliance", retention: "7y"},
        },
        {
            uid: "4",
            display: "Audio Transcript — Interview 0042",
            typeKey: "audio",
            definitionKey: "interview_transcript",
            matchHash: "3a4b5c6d",
            frames: [
                {
                    uid: "f4",
                    frameNumber: 1,
                    page: 1, section: 3, paragraph: 1, sentence: 7,
                    originalText: "...and that's when I realized he had been at the meeting the whole time.",
                    pointers: [],
                },
            ],
            metadata: {duration: "00:42:18"},
        },
    ];
}

function UXIntelResourcesPanel() {
    const [references] = useState<ResourceReference[]>(buildTestData);
    const [filterText, setFilterText] = useState("");
    const [selectedUid, setSelectedUid] = useState<string | null>(null);

    const filtered = useMemo(() => {
        if (!filterText.trim()) return references;
        const q = filterText.toLowerCase();
        return references.filter((r) =>
            r.display.toLowerCase().includes(q)
            || r.typeKey.toLowerCase().includes(q)
            || r.definitionKey.toLowerCase().includes(q)
        );
    }, [references, filterText]);

    // Standard split-pane behavior: ensure selectedUid is always one of the
    // currently-visible items, auto-selecting the first when needed.
    useEffect(() => {
        if (filtered.length === 0) {
            if (selectedUid !== null) setSelectedUid(null);
            return;
        }
        const stillVisible = filtered.some((r) => r.uid === selectedUid);
        if (!stillVisible) {
            setSelectedUid(filtered[0].uid);
        }
    }, [filtered, selectedUid]);

    const selected = useMemo(() => {
        if (!selectedUid) return null;
        return filtered.find((r) => r.uid === selectedUid) || null;
    }, [filtered, selectedUid]);

    const handleSelect = (reference: ResourceReference) => {
        setSelectedUid(reference.uid);
    };

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Resource References</Typography>
            </Stack>

            {/* Split layout: card list (left 1/3) + detail pane (right 2/3, hidden if no items) */}
            <Stack direction="row" spacing={0} sx={{...appStyles.defaultPanelCardsWrapper, padding: "8px", alignItems: "stretch"}}>
                {/* Left: label + filter + card list — fixed-width 300px cards that flow 1-2 per row */}
                <Box sx={{
                    flex: selected ? "0 0 33.333%" : "1 1 100%",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    paddingRight: selected ? "12px" : 0,
                    borderRight: selected ? "1px solid #3F464F" : "none",
                }}>
                    <Box sx={{borderBottom: "1px solid #3F464F", paddingBottom: "6px", marginBottom: "10px"}}>
                        <Typography sx={{
                            color: "#a8b8c0",
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            letterSpacing: 0.3,
                            textAlign: "left",
                        }}>
                            Select a Reference Item
                        </Typography>
                    </Box>
                    {/* Filter bar — scoped to the reference item list */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{marginBottom: "10px", flexWrap: "wrap"}}>
                        <StyledSearch>
                            <StyledSearchIconWrapper>
                                <SearchIcon sx={{fontSize: 16, color: "#7093a0"}}/>
                            </StyledSearchIconWrapper>
                            <StyledInputBase
                                placeholder="Filter reference items..."
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                            />
                        </StyledSearch>
                        <Typography sx={appStyles.panelFilterCount}>
                            Showing {filtered.length} of {references.length}
                        </Typography>
                    </Stack>
                    {filtered.length === 0 ? (
                        <Typography sx={{
                            color: "rgba(242, 245, 250, 0.4)",
                            fontSize: "0.80rem",
                            textAlign: "center",
                            padding: "24px",
                        }}>
                            No items have been found
                        </Typography>
                    ) : (
                        <Box sx={{display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "flex-start"}}>
                            {filtered.map((ref) => (
                                <ResourceReferenceCard
                                    key={ref.uid}
                                    reference={ref}
                                    selected={selectedUid === ref.uid}
                                    onClick={handleSelect}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Right: detail pane (only when a card is selected) */}
                {selected && (
                    <Box sx={{
                        flex: "1 1 66.666%",
                        minWidth: 300,
                        display: "flex",
                        flexDirection: "column",
                        paddingLeft: "12px",
                    }}>
                        <Box sx={{borderBottom: "1px solid #3F464F", paddingBottom: "6px", marginBottom: "10px"}}>
                            <Typography sx={{
                                color: "#a8b8c0",
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                letterSpacing: 0.3,
                                textAlign: "left",
                            }}>
                                Details for Selected Reference
                            </Typography>
                        </Box>
                        <Box sx={{flex: 1, minHeight: 0}}>
                            <ResourceReferenceDetail reference={selected}/>
                        </Box>
                    </Box>
                )}
            </Stack>
        </Stack>
    );
}

export default UXIntelResourcesPanel;
